import { Button, Box, Typography, Avatar } from "@mui/material";
import React from "react";
import { formatNotificationDate } from "../../Utils/dateFormat";
import { getInterpolatedNotification } from "../../Utils/notifications";

const primaryBtnStyles = {
  width: "max-content",
  borderRadius: "4px",
  px: "8px",
  py: "6px",
  fontSize: "13px",
  fontWeight: 500,
  lineHeight: "18px",
  color: "rgba(255, 255, 255, 1)",
  backgroundColor: "rgba(36, 139, 112, 1)",
  boxShadow: "none !important",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "rgba(30, 109, 91, 1)",
  },
};

const secondaryBtnStyles = {
  width: "max-content",
  backgroundColor: "rgba(242, 243, 251, 1)",
  color: "rgba(36, 139, 112, 1)",
  textTransform: "none",
  boxShadow: "none !important",
  px: "8px",
  py: "6px",
  borderRadius: "4px",
  fontSize: "13px",
  fontWeight: 500,
  lineHeight: "18px",
  "&:hover": {
    backgroundColor: "rgba(221, 233, 232, 1)",
  },
};

/**
 * A single notification recipient row in the panel.
 * Renders the interpolated i18n copy (DB-template fallback), the read-state
 * dot (click toggles read/unread), avatar from image_uid, date, and the CTA:
 * a button (navigate/copy/dynamic/tos), contact-info icons (customCta), and
 * an optional secondary button.
 */
function NotificationItem({
  notification,
  isFirstItem,
  isBelowMd,
  isRead = false,
  onCtaClick,
  onSecondaryCtaClick,
  onMarkRead,
  onToggleRead,
  t,
  customCta,
}) {
  const { creation_time, image_uid } = notification;
  const { title, content, cta, cta2 } = getInterpolatedNotification(
    notification,
    t,
  );

  // Public (no-auth) file route — <img> requests from another port don't
  // reliably carry the auth cookie
  const imageUrl = image_uid
    ? `${process.env.REACT_APP_BASE_URL}public/files/view/${image_uid}`
    : undefined;

  const toggleLabel = isRead
    ? t("notifications-ui:markUnread")
    : t("notifications-ui:markRead");

  return (
    <Box
      data-recipient-id={notification.id}
      className="notification-item"
      sx={{
        py: "16px",
        px: { xs: "16px", md: "24px" },
        borderTop: isFirstItem ? "none" : "1px solid rgba(234, 234, 234, 1)",
        backgroundColor: "rgba(255, 255, 255, 1)",
        cursor: "pointer",
        transition: "background-color 0.2s",
        "&:hover": {
          backgroundColor: "rgba(249, 249, 252, 1)",
        },
      }}
      onClick={() => onMarkRead?.(notification)}
    >
      <Box sx={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
          {/* Read-state dot: filled green for unread, hollow for read; click toggles */}
          <Box
            component="button"
            type="button"
            aria-label={toggleLabel}
            title={toggleLabel}
            onClick={(e) => {
              e.stopPropagation();
              onToggleRead?.(notification, !isRead);
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "16px",
              height: "16px",
              m: "-4px",
              p: 0,
              border: "none",
              background: "none",
              cursor: "pointer",
            }}
          >
            <Box
              sx={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: isRead
                  ? "transparent"
                  : "rgba(36, 139, 112, 1)",
                border: isRead ? "1px solid rgba(72, 72, 72, 0.3)" : "none",
                opacity: isRead ? 0.5 : 1,
              }}
            />
          </Box>

          <Avatar
            alt={title?.replace(/[⁨⁩]/g, "").slice(0, 2) ?? "…"}
            src={imageUrl}
            sx={{ width: "32px", height: "32px" }}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "rgba(72, 72, 72, 0.9)",
                  fontWeight: 500,
                  lineHeight: "22px",
                }}
              >
                {title}
              </Typography>
              <Typography
                sx={{
                  fontSize: "13px",
                  color: "rgba(72, 72, 72, 0.7)",
                  fontWeight: 400,
                  lineHeight: "18px",
                }}
              >
                {content}
              </Typography>
            </Box>

            {isBelowMd && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: "13px",
                  color: "rgba(72, 72, 72, 0.4)",
                  fontWeight: 400,
                  lineHeight: "18px",
                }}
              >
                {formatNotificationDate(creation_time)}
              </Typography>
            )}

            {(customCta || cta || cta2) && (
              <Box
                sx={{
                  display: "flex",
                  gap: "8px",
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                {customCta ??
                  (cta && (
                    <Button
                      sx={primaryBtnStyles}
                      onClick={(e) => {
                        e.stopPropagation();
                        onCtaClick?.(notification);
                        onMarkRead?.(notification);
                      }}
                    >
                      {cta}
                    </Button>
                  ))}
                {cta2 && (
                  <Button
                    sx={secondaryBtnStyles}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSecondaryCtaClick?.(notification);
                    }}
                  >
                    {cta2}
                  </Button>
                )}
              </Box>
            )}
          </Box>
          {!isBelowMd && (
            <Typography
              variant="body2"
              sx={{
                fontSize: "13px",
                color: "rgba(72, 72, 72, 0.4)",
                fontWeight: 400,
                lineHeight: "18px",
                whiteSpace: "nowrap",
              }}
            >
              {formatNotificationDate(creation_time)}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default NotificationItem;
